import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, takeUntil, Subject } from 'rxjs';
import { ChatWindowComponent } from '../../chat/chat-window/chat-window.component';
import { ChannelListComponent } from '../../chat/channel-list/channel-list.component';
import { Channel } from '../models/chat.models';
import {
  ChatService,
  FooterChannelsSubTab,
} from '../services/chat.service';

export type FooterChatTab = 'chats' | 'channels' | 'contacts';
export type ConversationSize = 'default' | 'expanded';

export interface ChatNotificationToast {
  id: string;
  senderName: string;
  senderAvatar?: string | null;
  senderInitials: string;
  messageText: string;
  channelId: number;
  createdAt: Date;
  timer?: any;
}

export interface DockConversationWindow {
  channel: Channel;
  size: ConversationSize;
  minimized: boolean;
  avatarFailed: boolean;
  openedAt: number;
}

const DOCK_UNIT_WIDTH = 345;
const WINDOW_WIDTH = 340;
const WINDOW_GAP = 5;
const MIN_DOCK_WIDTH_FOR_WINDOWS = 768;
const SAFETY_MARGIN = 10;

@Component({
  standalone: true,
  selector: 'app-footer-chat-dock',
  templateUrl: './footer-chat-dock.component.html',
  styleUrls: ['./footer-chat-dock.component.scss'],
  imports: [CommonModule, FormsModule, ChannelListComponent, ChatWindowComponent],
})
export class FooterChatDockComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(ChatWindowComponent)
  chatWindows!: QueryList<ChatWindowComponent>;

  @ViewChild('footerChatDock')
  footerChatDockRef?: ElementRef<HTMLElement>;

  panelOpen = false;
  activeTab: FooterChatTab = 'chats';
  channelsSubTabPreference: FooterChannelsSubTab = 'group';
  openWindows: DockConversationWindow[] = [];
  openChannelIds: number[] = [];
  focusedChannelId: number | null = null;
  searchQuery = '';
  actionsMenuOpen = false;

  maxOpenWindows = 2;

  userName = '';
  userInitials = '';

  activeToasts: ChatNotificationToast[] = [];

  private pendingParticipantsChannelId: number | null = null;
  directUnread = 0;
  channelUnread = 0;
  connectionStatus: 'online' | 'offline' | 'connecting' | 'connected' | 'reconnecting' =
    'connecting';
  private unreadSubs: Subscription[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    public router: Router,
    private chatService: ChatService
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    this.recalculateMaxWindows();

    this.chatService.openFooterChat$
      .pipe(takeUntil(this.destroy$))
      .subscribe((request) => {
        this.panelOpen = true;
        if (request.channelsSubTab) {
          this.channelsSubTabPreference = request.channelsSubTab;
        }

        if (request.channelId) {
          this.openChannelById(request.channelId);
        }
      });

    this.chatService.inAppNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ message, channelId }) => {
        this.handleInAppNotification(message, channelId);
      });

    if (this.chatService.hasAuthContext()) {
      this.chatService.connectWebSocket();
      // Keep unread badges alive while the channel-list panel is closed.
      this.chatService.bootstrapUnreadTracking();
      this.unreadSubs.push(
        this.chatService.directUnread$.subscribe((count) => {
          this.directUnread = count;
        }),
        this.chatService.channelUnread$.subscribe((count) => {
          this.channelUnread = count;
        }),
        this.chatService.connectionStatus$.subscribe((status) => {
          this.connectionStatus = status;
        })
      );
    }
  }

  ngAfterViewInit(): void {
    this.recalculateMaxWindows();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.recalculateMaxWindows();
  }

  private recalculateMaxWindows(): void {
    if (typeof window === 'undefined') return;

    const dockEl = this.footerChatDockRef?.nativeElement;
    // dockEl.clientWidth = vw - sidebarWidth (CSS already handles sidebar offset)
    const dockWidth = dockEl
      ? dockEl.clientWidth
      : Math.max(0, window.innerWidth - 58); // 58px = compact sidebar fallback

    if (dockWidth < MIN_DOCK_WIDTH_FOR_WINDOWS) {
      this.maxOpenWindows = 1;
    } else {
      const availableWidth = Math.max(0, dockWidth - DOCK_UNIT_WIDTH - SAFETY_MARGIN);
      const count = Math.max(
        1,
        Math.floor((availableWidth + WINDOW_GAP) / (WINDOW_WIDTH + WINDOW_GAP))
      );
      this.maxOpenWindows = count;
    }
    this.trimExcessWindows();
  }

  private trimExcessWindows(): void {
    while (this.openWindows.length > this.maxOpenWindows) {
      const oldest = this.openWindows.reduce((prev, curr) =>
        curr.openedAt < prev.openedAt ? curr : prev
      );
      this.closeChatWindow(oldest.channel.id);
    }
  }

  ngOnDestroy(): void {
    this.unreadSubs.forEach((sub) => sub.unsubscribe());
    this.chatService.setActiveChannel(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeChannelId(): number | null {
    return this.focusedChannelId;
  }

  trackWindow(_index: number, win: DockConversationWindow): number {
    return win.channel.id;
  }

  private syncOpenChannelIds(): void {
    this.openChannelIds = this.openWindows.map((win) => win.channel.id);
  }

  private openChannelById(channelId: number): void {
    this.chatService.getChannelDetail(channelId).subscribe({
      next: (detail) => {
        const type = detail.channel.channel_type;
        if (type === 'direct') {
          this.activeTab = 'chats';
        } else if (type === 'entity_linked') {
          this.activeTab = 'channels';
          this.channelsSubTabPreference = 'entity_linked';
        } else if (type === 'group') {
          this.activeTab = 'channels';
          this.channelsSubTabPreference = 'group';
        }
        this.onChannelSelected(detail.channel);
      },
    });
  }

  private handleInAppNotification(message: any, channelId: number): void {
    const senderName = message?.sender_name || 'SD books';
    let textContent = message?.message_text || '';

    if (textContent && textContent.includes('<')) {
      const tmp = document.createElement('DIV');
      tmp.innerHTML = textContent;
      textContent = (
        tmp.textContent ||
        tmp.innerText ||
        textContent.replace(/<[^>]*>?/gm, '')
      ).trim();
    }

    if (!textContent && message?.attachment_name) {
      textContent = `📎 Attachment: ${message.attachment_name}`;
    } else if (!textContent && message?.message_type === 'file') {
      textContent = '📎 Sent a file';
    }

    if (textContent.length > 100) {
      textContent = textContent.substring(0, 97) + '...';
    }

    const toastId = 'toast_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6);

    const toast: ChatNotificationToast = {
      id: toastId,
      senderName,
      senderAvatar: message?.sender_avatar || null,
      senderInitials: this.getToastInitials(senderName),
      messageText: textContent,
      channelId,
      createdAt: new Date(),
    };

    toast.timer = setTimeout(() => {
      this.dismissToast(toastId);
    }, 10000);

    this.activeToasts.unshift(toast);
    if (this.activeToasts.length > 3) {
      const removed = this.activeToasts.pop();
      if (removed?.timer) {
        clearTimeout(removed.timer);
      }
    }
  }

  dismissToast(toastId: string, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    const idx = this.activeToasts.findIndex((t) => t.id === toastId);
    if (idx !== -1) {
      if (this.activeToasts[idx].timer) {
        clearTimeout(this.activeToasts[idx].timer);
      }
      this.activeToasts.splice(idx, 1);
    }
  }

  onToastClick(toast: ChatNotificationToast): void {
    this.dismissToast(toast.id);
    this.panelOpen = true;
    this.openChannelById(toast.channelId);
  }

  private getToastInitials(name: string): string {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  get showDock(): boolean {
    const url = this.router.url;
    return !(
      url.includes('/maintainance/maintainance-page') ||
      url.includes('/contacts/contact-detail') ||
      url.includes('/error/error-page') ||
      url.includes('/login') ||
      url.includes('/chat')
    );
  }

  openTab(tab: FooterChatTab): void {
    if (this.panelOpen && this.activeTab === tab) {
      this.minimizePanel();
      return;
    }
    if (tab === 'channels') {
      this.channelsSubTabPreference = 'group';
    }
    this.activeTab = tab;
    this.panelOpen = true;
  }

  minimizePanel(): void {
    this.panelOpen = false;
    this.clearSearch();
  }

  closeAll(): void {
    this.panelOpen = false;
    this.clearSearch();
    this.closeAllChatWindows();
  }

  private clearSearch(): void {
    this.searchQuery = '';
  }

  onChannelSelected(channel: Channel): void {
    const existing = this.openWindows.find((win) => win.channel.id === channel.id);
    if (existing) {
      existing.channel = channel;
      existing.avatarFailed = false;
      this.activateConversation(channel.id);
      this.tryOpenPendingParticipants(channel.id);
      return;
    }

    if (this.openWindows.length >= this.maxOpenWindows) {
      const oldest = this.openWindows.reduce((prev, curr) =>
        curr.openedAt < prev.openedAt ? curr : prev
      );
      this.closeChatWindow(oldest.channel.id);
    }

    const window: DockConversationWindow = {
      channel,
      size: 'default',
      minimized: false,
      avatarFailed: false,
      openedAt: Date.now(),
    };
    this.openWindows = [...this.openWindows, window];
    this.syncOpenChannelIds();
    this.activateConversation(channel.id);
    this.tryOpenPendingParticipants(channel.id);
  }

  private activateConversation(channelId: number): void {
    for (const win of this.openWindows) {
      if (win.channel.id === channelId) {
        win.minimized = false;
        this.chatService.setActiveChannel(win.channel.id, true);
        this.chatService.clearUnread(win.channel.id);
      }
    }
    this.focusedChannelId = channelId;
  }

  closeChatWindow(channelId: number): void {
    const win = this.openWindows.find((w) => w.channel.id === channelId);
    if (!win) {
      return;
    }

    this.chatService.setActiveChannel(channelId, false);
    this.openWindows = this.openWindows.filter((w) => w.channel.id !== channelId);
    this.syncOpenChannelIds();

    if (this.focusedChannelId === channelId) {
      const newest = this.openWindows.length
        ? this.openWindows[this.openWindows.length - 1]
        : null;
      this.focusedChannelId = newest?.channel.id ?? null;
    }

    if (this.pendingParticipantsChannelId === channelId) {
      this.pendingParticipantsChannelId = null;
    }
  }

  closeAllChatWindows(): void {
    for (const win of this.openWindows) {
      this.chatService.setActiveChannel(win.channel.id, false);
    }
    this.openWindows = [];
    this.syncOpenChannelIds();
    this.focusedChannelId = null;
    this.actionsMenuOpen = false;
    this.pendingParticipantsChannelId = null;
  }

  focusWindow(channelId: number): void {
    const win = this.openWindows.find((w) => w.channel.id === channelId);
    if (!win || win.minimized) {
      return;
    }
    this.activateConversation(channelId);
  }

  toggleExpand(channelId: number): void {
    const win = this.openWindows.find((w) => w.channel.id === channelId);
    if (!win || win.minimized) {
      return;
    }
    win.size = win.size === 'expanded' ? 'default' : 'expanded';
  }

  minimizeConversation(channelId: number): void {
    const win = this.openWindows.find((w) => w.channel.id === channelId);
    if (!win) {
      return;
    }
    win.minimized = true;
    this.actionsMenuOpen = false;
    this.chatService.setActiveChannel(channelId, false);
  }

  onMinimizedHeaderClick(channelId: number): void {
    const win = this.openWindows.find((w) => w.channel.id === channelId);
    if (!win || !win.minimized) {
      return;
    }

    this.activateConversation(channelId);

    setTimeout(() => {
      this.getChatWindow(channelId)?.markCurrentChannelRead();
    }, 0);
  }

  private getChatWindow(channelId: number): ChatWindowComponent | undefined {
    return this.chatWindows?.find((ref) => ref.channel?.id === channelId);
  }

  private tryOpenPendingParticipants(channelId: number): void {
    if (this.pendingParticipantsChannelId !== channelId) {
      return;
    }
    setTimeout(() => {
      const ref = this.getChatWindow(channelId);
      if (ref) {
        this.pendingParticipantsChannelId = null;
        ref.openParticipantsDialog();
      }
    }, 0);
  }

  getChannelTitle(win: DockConversationWindow): string {
    const ref = this.getChatWindow(win.channel.id);
    if (ref) {
      return ref.getChannelTitle();
    }
    if (win.channel.channel_name) return win.channel.channel_name;
    if (win.channel.channel_type === 'direct') return 'Direct Message';
    return `Channel #${win.channel.id}`;
  }

  conversationAvatarUrl(win: DockConversationWindow): string | null {
    if (win.avatarFailed) {
      return null;
    }
    const ref = this.getChatWindow(win.channel.id);
    if (ref?.headerAvatarUrl) {
      return ref.headerAvatarUrl;
    }
    if (win.channel.profile_pic_url) {
      return win.channel.profile_pic_url;
    }
    return null;
  }

  onAvatarError(win: DockConversationWindow): void {
    win.avatarFailed = true;
  }

  conversationAvatarInitials(win: DockConversationWindow): string {
    const ref = this.getChatWindow(win.channel.id);
    if (ref) {
      return ref.headerAvatarInitials;
    }
    const title = this.getChannelTitle(win);
    const parts = title
      .split(' ')
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 2);
    return parts.map((part) => part.charAt(0).toUpperCase()).join('') || 'U';
  }

  showParticipantLink(win: DockConversationWindow): boolean {
    return (
      (win.channel.channel_type === 'group' ||
        win.channel.channel_type === 'entity_linked') &&
      !win.minimized
    );
  }

  participantCountLabel(win: DockConversationWindow): string {
    const ref = this.getChatWindow(win.channel.id);
    if (ref) {
      return ref.participantCountLabel;
    }
    const count = win.channel.member_count ?? 0;
    return `${count} participant${count === 1 ? '' : 's'}`;
  }

  openParticipantsDialog(channelId: number): void {
    const ref = this.getChatWindow(channelId);
    if (ref) {
      ref.openParticipantsDialog();
      return;
    }
    this.pendingParticipantsChannelId = channelId;
  }

  get connectionStatusLabel(): string {
    switch (this.connectionStatus) {
      case 'connected':
        return 'Available';
      case 'offline':
        return 'Offline';
      case 'reconnecting':
        return 'Reconnecting…';
      case 'connecting':
      case 'online':
        return 'Connecting…';
      default:
        return 'Available';
    }
  }

  userAvatarFailed = false;

  get userAvatarUrl(): string | null {
    if (this.userAvatarFailed) {
      return null;
    }
    try {
      const raw = localStorage.getItem('optplanner_user');
      if (!raw) return null;
      const user = JSON.parse(raw);
      return user.profile_pic_url || user.profile_pic || user.profilePicUrl || null;
    } catch {
      return null;
    }
  }

  onUserAvatarError(): void {
    this.userAvatarFailed = true;
  }

  private loadUserInfo(): void {
    try {
      const raw = localStorage.getItem('optplanner_user');
      if (!raw) return;
      const user = JSON.parse(raw);
      const first = user.firstName || user.first_name || '';
      const last = user.lastName || user.last_name || '';
      this.userName = `${first} ${last}`.trim() || 'User';
      this.userInitials =
        `${(first || 'U').charAt(0)}${(last || '').charAt(0)}`.toUpperCase() || 'U';
    } catch {
      this.userName = 'User';
      this.userInitials = 'U';
    }
  }
}
